import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { HiChevronLeft } from "react-icons/hi";
import { HiArrowPath, HiCalendarDays } from "react-icons/hi2";
import { Dialog, Transition } from "@headlessui/react";
import {
  ActivityMeasurement,
  ActivityBlock,
  SessionTemplate,
  PeriodisationType,
} from "../../types/post";
import { TrainingType } from "../../types/activity";
import { sessionService } from "../../services/sessionService";
import { trainingTypeService } from "../../services/trainingTypeService";
import { asDisplayNameOrNull } from "../../utils/displayName";
import MasterSessionDetails from "./MasterSessionDetails";
import SearchableSelect from "../common/SearchableSelect";
import SessionNotesEditor from "./SessionNotesEditor";
import AILogButton, { type AILogFunction } from "../AILogButton";
import {
  HiOutlinePencilSquare,
  HiOutlineSquares2X2,
  HiOutlineDocumentText,
} from "react-icons/hi2";
import { TEXT_BUTTON_CLASS } from "../../utils/buttonStyles";

/**
 * How the athlete supplies the session's content on Step 1:
 *   - scratch:  build activities from an empty session (default)
 *   - template: start from a saved session template and tweak it
 *   - notes:    write session notes for a session with no tracked activities
 */
type SourceMode = "scratch" | "template" | "notes";

export interface PlannedSessionInput {
  ActivityMeasurementList?: ActivityMeasurement[];
  activityBlocks?: ActivityBlock[];
  plannedDate: string;
  name?: string;
  description: string;
  trainingTypeID?: string;
  periodisation?: PeriodisationType;
}

interface PlanSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionCreated?: (sessionIds: string[]) => void;
  initialTemplate?: (SessionTemplate & { id: string }) | null;
  /** Optional YYYY-MM-DD date string to pre-fill the planned date when opening the modal */
  initialPlannedDate?: string | null;
  saveSessions?: (sessions: PlannedSessionInput[]) => Promise<string[]>;
  navigateOnSave?: boolean;
  minPlannedDate?: string;
  maxPlannedDate?: string;
  allowedPlannedDates?: string[];
}

const PlanSessionModal: React.FC<PlanSessionModalProps> = ({
  isOpen,
  onClose,
  onSessionCreated,
  initialTemplate,
  initialPlannedDate,
  saveSessions,
  navigateOnSave = true,
  minPlannedDate,
  maxPlannedDate,
  allowedPlannedDates,
}) => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();

  // Hidden native date input behind the clickable header date (log-form style).
  const headerDateInputRef = useRef<HTMLInputElement>(null);

  // Human-friendly label for the header date (e.g. "Wed, 22 Jul 2026").
  const formatPlannedDateLabel = (dateStr: string): string => {
    if (!dateStr) return "Select a date";
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) return "Select a date";
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getLocalDateString = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDefaultPlannedDate = () => {
    const fromProp = initialPlannedDate?.trim();
    return clampDateToAllowed(fromProp || getLocalDateString(new Date()));
  };

  // Form state
  const [sessionTitle, setSessionTitle] = useState<string>("");
  const [plannedDate, setPlannedDate] = useState<string>("");
  const [plannedMeasurements, setPlannedMeasurements] = useState<
    ActivityMeasurement[]
  >([]);
  const [trainingTypeID, setTrainingTypeID] = useState<string>("");
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [periodisation, setPeriodisation] = useState<PeriodisationType | "">(
    "",
  );
  const [sessionDescription, setSessionDescription] = useState<string>("");

  // How the session content is supplied on Step 1. Defaults to building from
  // scratch; the athlete can switch to a template or plain session notes.
  const [sourceMode, setSourceMode] = useState<SourceMode>("scratch");
  // Inline session-notes markdown (notes source mode). Synthesised into a
  // "Session Note" activity block on save so it renders like a coach note.
  const [notesContent, setNotesContent] = useState<string>("");
  // Saved session templates available to start from (template source mode).
  const [sessionTemplates, setSessionTemplates] = useState<
    (SessionTemplate & { id: string })[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Loading and error states
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Confirmation dialog state
  const [showResetConfirmation, setShowResetConfirmation] =
    useState<boolean>(false);

  // Step state (1 = activities, 2 = dates)
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Recurring sessions state
  const [selectedDays, setSelectedDays] = useState<number[]>([]); // 0 = Sunday, 1 = Monday, etc.
  const [recurringWeeks, setRecurringWeeks] = useState<number>(4);
  const [recurringMode, setRecurringMode] = useState<
    "single" | "weekly" | "specific"
  >("single");
  const [selectedSpecificDates, setSelectedSpecificDates] = useState<string[]>(
    [],
  );

  // Reset key to force remount of MasterSessionDetails
  const [resetKey, setResetKey] = useState<number>(0);
  // Refresh key to force re-fetching of session templates when modal opens
  const [templateRefreshKey, setTemplateRefreshKey] = useState<number>(0);

  // AI dictation state
  const [aiStatus, setAiStatus] = useState<"idle" | "connecting" | "listening">(
    "idle",
  );
  const [aiPrompt, setAiPrompt] = useState<string>("");
  const [aiLogFunctions, setAiLogFunctions] = useState<AILogFunction[]>([]);

  const handleAIConfigReady = useCallback(
    (prompt: string, logFunctions: AILogFunction[]) => {
      setAiPrompt(prompt);
      setAiLogFunctions(logFunctions);
    },
    [],
  );

  const allowedDateSet = allowedPlannedDates
    ? new Set(allowedPlannedDates.filter((date) => !!date))
    : null;

  const isDateAllowed = (dateStr: string) => {
    if (minPlannedDate && dateStr < minPlannedDate) return false;
    if (maxPlannedDate && dateStr > maxPlannedDate) return false;
    if (allowedDateSet && !allowedDateSet.has(dateStr)) return false;
    return true;
  };

  const clampDateToAllowed = (dateStr: string) => {
    const candidate = dateStr?.trim();
    if (candidate && isDateAllowed(candidate)) {
      return candidate;
    }

    if (allowedPlannedDates?.length) {
      return allowedPlannedDates[0];
    }

    if (minPlannedDate) {
      return minPlannedDate;
    }

    if (maxPlannedDate) {
      return maxPlannedDate;
    }

    return candidate || getLocalDateString(new Date());
  };

  const buildSessionPayload = (date: string): PlannedSessionInput => {
    // Notes mode carries no tracked activities — the content lives in a single
    // "Session Note" block so it renders like a coach-authored note.
    const isNotes = sourceMode === "notes";
    const sessionData: PlannedSessionInput = {
      ActivityMeasurementList: isNotes ? [] : plannedMeasurements,
      plannedDate: date,
      name: sessionTitle.trim() || "",
      description: sessionDescription.trim(),
    };

    if (isNotes && notesContent.trim()) {
      sessionData.activityBlocks = [
        {
          id: crypto.randomUUID(),
          name: "Session Note",
          ActivityMeasurementList: [],
          blockType: "notes",
          notesContent: notesContent.trim(),
        },
      ];
    }

    if (trainingTypeID && trainingTypeID.trim()) {
      sessionData.trainingTypeID = trainingTypeID.trim();
    }
    if (periodisation) {
      sessionData.periodisation = periodisation;
    }

    return sessionData;
  };

  const persistSessions = async (sessions: PlannedSessionInput[]) => {
    if (saveSessions) {
      return saveSessions(sessions);
    }

    if (!currentUser?.uid) {
      throw new Error("You must be logged in to plan a session");
    }

    return Promise.all(
      sessions.map((sessionData) =>
        sessionService.createSession(currentUser.uid!, sessionData as any),
      ),
    );
  };

  const completeSessionCreation = (sessionIds: string[]) => {
    if (onSessionCreated) {
      onSessionCreated(sessionIds);
    }

    onClose();

    if (navigateOnSave) {
      navigate("/sessions");
    }
  };

  // Refresh session templates when modal opens
  useEffect(() => {
    if (isOpen) {
      setTemplateRefreshKey((prev) => prev + 1);
    }
  }, [isOpen]);

  // Fetch training types when modal opens
  useEffect(() => {
    const fetchTrainingTypes = async () => {
      if (!isOpen) return;

      try {
        const types = await trainingTypeService.fetchTrainingTypesForSports(
          userData?.trainingSportIDs,
          userData?.customTrainingTypes,
        );
        setTrainingTypes(types);
      } catch (error) {
        console.error("Error fetching training types:", error);
      }
    };

    fetchTrainingTypes();
  }, [isOpen, userData?.trainingSportIDs, userData?.customTrainingTypes]);

  // Fetch saved session templates when the modal opens (template source mode)
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!isOpen || !currentUser?.uid) return;
      try {
        const templates = await sessionService.getAllSessionTemplates(
          currentUser.uid,
        );
        setSessionTemplates(
          (templates || []).filter(
            (t): t is SessionTemplate & { id: string } => !!t.id,
          ),
        );
      } catch (error) {
        console.error("Error fetching session templates:", error);
      }
    };

    fetchTemplates();
  }, [isOpen, currentUser?.uid, templateRefreshKey]);

  // Initialize planned date when modal opens
  useEffect(() => {
    if (isOpen) {
      const nextPlannedDate = getDefaultPlannedDate();
      setPlannedDate(nextPlannedDate);
      setSelectedSpecificDates((currentDates) =>
        currentDates.filter((date) => isDateAllowed(date)),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    initialPlannedDate,
    minPlannedDate,
    maxPlannedDate,
    allowedPlannedDates,
  ]);

  // Resolve a template's training type (new trainingTypeID or legacy
  // composite sessionTypeID) to a trainingTypes doc ID for the picker.
  const resolveTemplateTrainingTypeID = (template: SessionTemplate): string => {
    return (
      trainingTypeService.resolveTrainingTypeForSession(
        template,
        trainingTypes,
      )?.id || ""
    );
  };

  // Initialize from template prop (re-runs when trainingTypes load so legacy
  // template IDs can be resolved)
  useEffect(() => {
    if (initialTemplate) {
      setPlannedMeasurements(initialTemplate.ActivityMeasurementList || []);
      // If the template has a name/title, pre-fill it so scheduled sessions inherit it
      setSessionTitle(asDisplayNameOrNull(initialTemplate.name) || "");
      setTrainingTypeID(resolveTemplateTrainingTypeID(initialTemplate));
      setPeriodisation(initialTemplate.periodisation || "");
      setSessionDescription(initialTemplate.description?.trim() || "");
      // Arriving with a template in hand pre-selects the template source.
      setSourceMode("template");
      if (initialTemplate.id) setSelectedTemplateId(initialTemplate.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplate, trainingTypes]);

  // Choose how to supply the session content. Switching away from a mode
  // clears state that no longer applies so each mode starts clean.
  const chooseSource = (mode: SourceMode) => {
    if (mode === sourceMode) return;
    setSourceMode(mode);
    setError(null);
    if (mode !== "template") {
      setSelectedTemplateId("");
    }
    if (mode === "notes") {
      // Notes have no tracked activities.
      setPlannedMeasurements([]);
    } else {
      setNotesContent("");
    }
    if (mode === "scratch") {
      // Fresh scratch build — drop any template-derived content.
      setPlannedMeasurements([]);
      setSelectedTemplateId("");
      setResetKey((prev) => prev + 1);
    }
  };

  // Load a saved template into the working session (template source mode).
  const handleTemplatePick = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setPlannedMeasurements([]);
      setSessionTitle("");
      setTrainingTypeID("");
      setPeriodisation("");
      setSessionDescription("");
      setResetKey((prev) => prev + 1);
      return;
    }
    const template = sessionTemplates.find((t) => t.id === templateId);
    if (!template) return;
    setPlannedMeasurements(template.ActivityMeasurementList || []);
    setSessionTitle(asDisplayNameOrNull(template.name) || "");
    setTrainingTypeID(resolveTemplateTrainingTypeID(template));
    setPeriodisation(template.periodisation || "");
    setSessionDescription(template.description?.trim() || "");
    setResetKey((prev) => prev + 1);
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSessionTitle("");
      setPlannedDate("");
      setPlannedMeasurements([]);
      setTrainingTypeID("");
      setPeriodisation("");
      setSessionDescription("");
      setError(null);
      setCurrentStep(1);
      setRecurringMode("single");
      setSelectedDays([]);
      setSelectedSpecificDates([]);
      setResetKey((prev) => prev + 1);
      setSourceMode("scratch");
      setNotesContent("");
      setSelectedTemplateId("");
    }
  }, [isOpen]);

  const handlePlanSession = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to plan a session");
      return;
    }

    if (!plannedDate) {
      setError("Please select a planned date");
      return;
    }

    if (sourceMode === "notes") {
      if (!notesContent.trim()) {
        setError("Please write some session notes");
        return;
      }
    } else if (plannedMeasurements.length === 0) {
      setError("Please add at least one activity to the session");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const sessionIds = await persistSessions([
        buildSessionPayload(plannedDate),
      ]);
      completeSessionCreation(sessionIds);
    } catch (error) {
      console.error("Error planning session:", error);
      setError("Failed to plan session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSessionTitle("");
    setPlannedDate(getDefaultPlannedDate());
    setPlannedMeasurements([]);
    setTrainingTypeID("");
    setPeriodisation("");
    setSessionDescription("");
    setError(null);
    setShowResetConfirmation(false);
    setCurrentStep(1);
    setRecurringMode("single");
    setSelectedDays([]);
    setSelectedSpecificDates([]);
    setSourceMode("scratch");
    setNotesContent("");
    setSelectedTemplateId("");
    // Increment resetKey to force remount of MasterSessionDetails
    setResetKey((prev) => prev + 1);
  };

  // Toggle day selection for recurring sessions
  const toggleDay = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  // Toggle specific date selection
  const toggleSpecificDate = (dateStr: string) => {
    setSelectedSpecificDates((prev) =>
      prev.includes(dateStr)
        ? prev.filter((d) => d !== dateStr)
        : [...prev, dateStr],
    );
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get calendar grid for the available dates, aligned with days of the week (Monday start)
  const getCalendarGrid = (): (null | { dateStr: string; day: number })[][] => {
    if (allowedPlannedDates?.length) {
      const sortedDates = [...allowedPlannedDates].sort();
      const rows: (null | { dateStr: string; day: number })[][] = [];
      let currentWeek: (null | { dateStr: string; day: number })[] = new Array(
        7,
      ).fill(null);

      sortedDates.forEach((dateStr, index) => {
        const date = new Date(`${dateStr}T00:00:00`);
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;

        if (index === 0 && dayOfWeek > 0) {
          currentWeek = new Array(7).fill(null);
        }

        currentWeek[dayOfWeek] = {
          dateStr,
          day: date.getDate(),
        };

        if (dayOfWeek === 6) {
          rows.push(currentWeek);
          currentWeek = new Array(7).fill(null);
        }
      });

      if (currentWeek.some((cell) => cell !== null)) {
        rows.push(currentWeek);
      }

      if (rows.length === 0) {
        rows.push(new Array(7).fill(null));
      }

      return rows;
    }

    const startDate = minPlannedDate
      ? new Date(`${minPlannedDate}T00:00:00`)
      : new Date();
    startDate.setHours(0, 0, 0, 0);

    const totalSelectableDays = allowedPlannedDates?.length
      ? allowedPlannedDates.length
      : maxPlannedDate &&
          maxPlannedDate >= clampDateToAllowed(getLocalDateString(startDate))
        ? Math.max(
            1,
            Math.floor(
              (new Date(`${maxPlannedDate}T00:00:00`).getTime() -
                startDate.getTime()) /
                (1000 * 60 * 60 * 24),
            ) + 1,
          )
        : 28;

    const rows: (null | { dateStr: string; day: number })[][] = [];
    let currentDate = new Date(startDate);
    let daysAdded = 0;

    // Calculate how many empty cells we need at the start (Monday = 0, Sunday = 6)
    // JavaScript getDay() returns 0 for Sunday, 1 for Monday, etc.
    // Convert to Monday-based: Monday=0, Tuesday=1, ..., Sunday=6
    const jsDayOfWeek = startDate.getDay();
    const startDayOfWeek = jsDayOfWeek === 0 ? 6 : jsDayOfWeek - 1;

    while (daysAdded < totalSelectableDays) {
      const row: (null | { dateStr: string; day: number })[] = [];

      for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
        if (rows.length === 0 && dayOfWeek < startDayOfWeek) {
          // First week: empty cells before today
          row.push(null);
        } else if (daysAdded < totalSelectableDays) {
          const dateStr = formatLocalDate(currentDate);
          row.push(
            isDateAllowed(dateStr)
              ? {
                  dateStr,
                  day: currentDate.getDate(),
                }
              : null,
          );
          currentDate.setDate(currentDate.getDate() + 1);
          daysAdded++;
        } else {
          row.push(null);
        }
      }

      rows.push(row);
    }

    return rows;
  };

  // Generate dates for recurring sessions
  const getRecurringDates = (): string[] => {
    if (recurringMode === "specific") {
      return selectedSpecificDates.filter((date) => isDateAllowed(date)).sort();
    }

    if (selectedDays.length === 0) return [];

    const dates: string[] = [];
    const anchor = new Date(
      `${clampDateToAllowed(plannedDate || getDefaultPlannedDate())}T00:00:00`,
    );
    anchor.setHours(0, 0, 0, 0);
    const currentDay = anchor.getDay();

    for (let week = 0; week < recurringWeeks; week++) {
      const weekStart = new Date(anchor);
      weekStart.setDate(anchor.getDate() + week * 7);

      for (const day of selectedDays) {
        const date = new Date(weekStart);
        let daysUntil = day - currentDay;
        if (daysUntil < 0) {
          daysUntil += 7;
        }

        date.setDate(weekStart.getDate() + daysUntil);
        const dateStr = formatLocalDate(date);

        if (isDateAllowed(dateStr)) {
          dates.push(dateStr);
        }
      }
    }

    // Sort and remove duplicates
    return Array.from(new Set(dates)).sort();
  };

  // Create recurring sessions
  const handleCreateRecurringSessions = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to plan a session");
      return;
    }
    if (sourceMode === "notes") {
      if (!notesContent.trim()) {
        setError("Please write some session notes before scheduling");
        return;
      }
    } else if (plannedMeasurements.length === 0) {
      setError("Please add activities before creating recurring sessions");
      return;
    }

    const dates = getRecurringDates();
    if (dates.length === 0) {
      setError("Please select at least one day");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const sessionIds = await persistSessions(
        dates.map((date) => buildSessionPayload(date)),
      );

      // Reset and close
      setSelectedDays([]);
      setSelectedSpecificDates([]);
      completeSessionCreation(sessionIds);
    } catch (error) {
      console.error("Error creating recurring sessions:", error);
      setError("Failed to create recurring sessions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isStep1Valid =
    sourceMode === "notes"
      ? notesContent.trim().length > 0
      : plannedMeasurements.length > 0;

  // The three ways to supply the session's content, shown as a selector on
  // Step 1. Scratch is the default; template and notes are the alternatives.
  const sourceOptions: {
    mode: SourceMode;
    icon: React.ReactNode;
    label: string;
  }[] = [
    {
      mode: "scratch",
      icon: <HiOutlinePencilSquare className="h-5 w-5" />,
      label: "From scratch",
    },
    {
      mode: "template",
      icon: <HiOutlineSquares2X2 className="h-5 w-5" />,
      label: "Template",
    },
    {
      mode: "notes",
      icon: <HiOutlineDocumentText className="h-5 w-5" />,
      label: "Session notes",
    },
  ];
  const isStep2Valid =
    recurringMode === "single"
      ? !!plannedDate
      : recurringMode === "weekly"
        ? getRecurringDates().length > 0
        : getRecurringDates().length > 0;

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 backdrop-blur-sm overflow-hidden">
      <div className="flex flex-col h-full md:items-center md:justify-center md:overflow-auto">
        <div
          className="w-full max-w-2xl mx-auto bg-base-100 md:rounded-xl shadow-lg flex flex-col h-full md:h-auto md:max-h-[70vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - Fixed on mobile */}
          <div className="flex justify-between items-start p-4 pb-6 md:p-6 md:pb-8 flex-shrink-0 bg-base-100 md:rounded-t-xl">
            <div className="flex items-start gap-2">
              <button
                onClick={onClose}
                disabled={loading}
                className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors disabled:opacity-50"
                style={{ touchAction: "manipulation" }}
              >
                <HiChevronLeft className="h-8 w-8 text-base-content/70" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-base-content font-heading">
                  Plan session
                </h2>
                {/* Clickable date — sets the day this session is planned for
                    (opens a native date picker, like the log forms). */}
                <div className="relative mt-1">
                  <button
                    type="button"
                    disabled={loading || recurringMode !== "single"}
                    onClick={() => {
                      const el = headerDateInputRef.current;
                      if (!el) return;
                      if (typeof el.showPicker === "function") {
                        el.showPicker();
                      } else {
                        el.focus();
                        el.click();
                      }
                    }}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-base-content/60 hover:text-base-content transition-colors disabled:opacity-60 disabled:cursor-not-allowed font-grotesk"
                  >
                    <HiCalendarDays className="h-3.5 w-3.5" />
                    <span className="underline decoration-dotted underline-offset-4">
                      {recurringMode === "single"
                        ? formatPlannedDateLabel(plannedDate)
                        : "Multiple dates (set below)"}
                    </span>
                  </button>
                  <input
                    ref={headerDateInputRef}
                    type="date"
                    value={plannedDate}
                    onChange={(e) =>
                      setPlannedDate(clampDateToAllowed(e.target.value))
                    }
                    min={minPlannedDate || getLocalDateString(new Date())}
                    max={maxPlannedDate}
                    className="absolute left-0 top-6 w-0 h-0 opacity-0 pointer-events-none"
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                </div>
              </div>
              <button
                onClick={() => setShowResetConfirmation(true)}
                disabled={loading}
                className="p-2 text-base-content/50 hover:text-base-content/80 rounded-full hover:bg-base-200 transition-colors disabled:opacity-50"
                title="Reset all fields"
              >
                <HiArrowPath className="text-lg" />
              </button>
            </div>
            <AILogButton
              prompt={aiPrompt}
              logFunctions={aiLogFunctions}
              title="Dictate session with AI"
              onStatusChange={(s) =>
                setAiStatus(
                  s === "listening"
                    ? "listening"
                    : s === "connecting"
                      ? "connecting"
                      : "idle",
                )
              }
            />
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-visible flex flex-col relative">
            {currentStep === 1 ? (
              <>
                {/* Step 1: choose how to build, then supply the content */}
                <div className="p-4 px-8 space-y-4 pb-0 bg-base-100">
                  {/* Source selector — matches the coach portal (scratch /
                      template / session notes). Defaults to "From scratch". */}
                  <div>
                    <p className="text-sm font-semibold text-base-content font-grotesk mb-2">
                      How do you want to build it?
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {sourceOptions.map((opt) => (
                        <button
                          key={opt.mode}
                          type="button"
                          onClick={() => chooseSource(opt.mode)}
                          disabled={loading}
                          className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border p-3 text-center transition-colors disabled:opacity-50 ${
                            sourceMode === opt.mode
                              ? "border-primary bg-primary/5 ring-1 ring-primary text-base-content"
                              : "border-base-300 bg-base-200/60 text-base-content/70 hover:border-base-content/30"
                          }`}
                        >
                          <span
                            className={
                              sourceMode === opt.mode
                                ? "text-primary"
                                : "text-base-content/60"
                            }
                          >
                            {opt.icon}
                          </span>
                          <span className="text-xs font-grotesk font-semibold leading-tight">
                            {opt.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Session Type Selector (optional for athlete-planned) */}
                  <SearchableSelect
                    options={[...trainingTypes]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((type) => ({
                        value: type.id || "",
                        label: type.name,
                      }))}
                    value={trainingTypeID}
                    onChange={setTrainingTypeID}
                    placeholder="Search session types..."
                    allOptionLabel="None"
                    emptyValue=""
                    disabled={loading}
                    label="Training Type"
                    inputClassName="bg-base-200"
                  />

                  {/* Template picker (template source mode) */}
                  {sourceMode === "template" && (
                    <SearchableSelect
                      options={[...sessionTemplates]
                        .sort((a, b) =>
                          (a.name || "Untitled").localeCompare(
                            b.name || "Untitled",
                          ),
                        )
                        .map((t) => ({
                          value: t.id,
                          label: `${t.name || "Untitled"} (${(t.ActivityMeasurementList || []).length} sets)`,
                        }))}
                      value={selectedTemplateId}
                      onChange={handleTemplatePick}
                      placeholder={
                        sessionTemplates.length > 0
                          ? "Search session templates..."
                          : "No templates available"
                      }
                      allOptionLabel="Select a session template..."
                      emptyValue=""
                      disabled={loading || sessionTemplates.length === 0}
                      label="Session Template"
                      inputClassName="bg-base-200"
                    />
                  )}
                </div>

                {/* Content: activities (scratch/template) or notes editor */}
                {sourceMode === "notes" ? (
                  <div className="p-4 px-8 pt-4 bg-base-100">
                    <label className="block text-sm font-semibold text-base-content font-grotesk mb-2">
                      Session notes
                    </label>
                    <SessionNotesEditor
                      value={notesContent}
                      onChange={setNotesContent}
                      placeholder="Write the session notes here..."
                    />
                  </div>
                ) : (
                  <div className="px-0 bg-base-100">
                    <MasterSessionDetails
                      key={`${sourceMode}-${selectedTemplateId}-${resetKey}`}
                      plannedMeasurements={plannedMeasurements}
                      onPlannedMeasurementsChange={setPlannedMeasurements}
                      startInBuildMode
                      hideTemplatePicker
                      loading={loading}
                      error={error}
                      onErrorChange={setError}
                      refreshKey={templateRefreshKey}
                      hideFloatingAIButton
                      onAIConfigReady={handleAIConfigReady}
                    />
                  </div>
                )}

                {/* Next Button */}
                <div
                  className="p-8 pb-12 flex-shrink-0 relative z-50 flex items-center gap-3 mt-auto"
                  style={{
                    pointerEvents: "auto",
                    touchAction: "manipulation",
                  }}
                >
                  {aiStatus !== "idle" ? (
                    <span className="flex-1 text-left text-base-content/80 font-grotesk text-sm">
                      {aiStatus === "connecting" ? (
                        <>
                          <span className="loading loading-spinner loading-xs mr-1"></span>
                          Loading...
                        </>
                      ) : (
                        <span className="flex items-center justify-between gap-2">
                          Listening...
                          <span className="inline-flex items-center gap-[3px] h-4">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <span
                                key={i}
                                className="w-[3px] rounded-full bg-red-500"
                                style={{
                                  animation: `listeningBar 1.2s ease-in-out ${i * 0.15}s infinite`,
                                  height: "4px",
                                }}
                              />
                            ))}
                          </span>
                          <style>{`
                            @keyframes listeningBar {
                              0%, 100% { height: 4px; }
                              50% { height: 16px; }
                            }
                          `}</style>
                        </span>
                      )}
                      <div className="text-xs text-base-content/50">
                        {aiStatus === "connecting"
                          ? "Setting up microphone."
                          : "Speak to log."}
                      </div>
                    </span>
                  ) : (
                    <button
                      onClick={() => setCurrentStep(2)}
                      disabled={loading || !isStep1Valid}
                      className="bg-primary text-white py-4 px-6 font-grotesk font-bold flex-1 rounded-xl disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
                      style={{ touchAction: "manipulation" }}
                    >
                      Next
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Step 2: Set Dates / Recurring Sessions */}
                <div className="p-4 px-8 space-y-4 bg-base-100">
                  {/* Date Selection - Always visible */}
                  <div>
                    <label className="block text-lg font-bold text-base-content mb-2">
                      When?
                    </label>
                    <input
                      type="date"
                      value={plannedDate}
                      onChange={(e) =>
                        setPlannedDate(clampDateToAllowed(e.target.value))
                      }
                      className="form-input bg-base-200 text-[16px] sm:text-sm cursor-pointer"
                      disabled={loading || recurringMode !== "single"}
                      min={minPlannedDate || getLocalDateString(new Date())}
                      max={maxPlannedDate}
                      style={{
                        colorScheme: "light",
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                      }}
                      onClick={(e) => {
                        if (recurringMode === "single") {
                          e.currentTarget.showPicker?.();
                        }
                      }}
                    />
                  </div>

                  {/* Recurring Options */}
                  <div className="pt-4 border-t border-base-200">
                    <p className="text-sm text-base-content/60 font-sans mb-3">
                      Or set up recurring sessions:
                    </p>
                    <div className="flex gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setRecurringMode(
                            recurringMode === "weekly" ? "single" : "weekly",
                          );
                          setSelectedDays([]);
                          setSelectedSpecificDates([]);
                        }}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                          recurringMode === "weekly"
                            ? "bg-base-content text-base-100"
                            : "bg-base-200 text-base-content/80 hover:bg-base-300"
                        }`}
                      >
                        Weekly
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRecurringMode(
                            recurringMode === "specific"
                              ? "single"
                              : "specific",
                          );
                          setSelectedDays([]);
                          setSelectedSpecificDates([]);
                        }}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg transition-colors ${
                          recurringMode === "specific"
                            ? "bg-base-content text-base-100"
                            : "bg-base-200 text-base-content/80 hover:bg-base-300"
                        }`}
                      >
                        Specific Days
                      </button>
                    </div>

                    {recurringMode === "weekly" && (
                      <>
                        <p className="text-sm text-base-content/60 font-sans mb-4">
                          Select the days you want to schedule this session:
                        </p>
                        <div className="grid grid-cols-7 gap-2 mb-6">
                          {["S", "M", "T", "W", "T", "F", "S"].map(
                            (label, index) => (
                              <button
                                key={index}
                                type="button"
                                onClick={() => toggleDay(index)}
                                className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                  selectedDays.includes(index)
                                    ? "bg-base-content text-base-100"
                                    : "bg-base-200 text-base-content/80 hover:bg-base-300"
                                }`}
                              >
                                {label}
                              </button>
                            ),
                          )}
                        </div>
                        <div className="mb-6">
                          <SearchableSelect
                            options={[
                              { value: "1", label: "1 week" },
                              { value: "2", label: "2 weeks" },
                              { value: "3", label: "3 weeks" },
                              { value: "4", label: "4 weeks" },
                            ]}
                            value={String(recurringWeeks)}
                            onChange={(value) =>
                              setRecurringWeeks(Number(value))
                            }
                            placeholder="Search..."
                            allOptionLabel="1 week"
                            emptyValue=""
                            label="Number of weeks"
                            inputClassName="bg-base-200"
                          />
                        </div>
                        {selectedDays.length > 0 && (
                          <div className="mb-4 p-3 bg-base-200 rounded-lg">
                            <p className="text-xs text-base-content/60 mb-1">
                              This will create {getRecurringDates().length}{" "}
                              sessions:
                            </p>
                            <ul className="mt-2 text-sm text-base-content space-y-1 max-h-32 overflow-y-auto">
                              {getRecurringDates().map((dateStr) => {
                                const date = new Date(dateStr + "T00:00:00");
                                return (
                                  <li key={dateStr}>
                                    {date.toLocaleDateString("en-AU", {
                                      weekday: "short",
                                      day: "numeric",
                                      month: "short",
                                    })}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </>
                    )}

                    {recurringMode === "specific" && (
                      <>
                        <p className="text-sm text-base-content/60 font-sans mb-4">
                          Select specific days:
                        </p>
                        <div className="mb-6">
                          <div className="grid grid-cols-7 gap-1 mb-2">
                            {["M", "T", "W", "T", "F", "S", "S"].map(
                              (label, index) => (
                                <div
                                  key={index}
                                  className="w-10 h-6 flex items-center justify-center text-xs font-medium text-base-content/60"
                                >
                                  {label}
                                </div>
                              ),
                            )}
                          </div>
                          {getCalendarGrid().map((week, weekIndex) => (
                            <div
                              key={weekIndex}
                              className="grid grid-cols-7 gap-1 mb-1"
                            >
                              {week.map((cell, dayIndex) => (
                                <button
                                  key={dayIndex}
                                  type="button"
                                  disabled={!cell}
                                  onClick={() =>
                                    cell && toggleSpecificDate(cell.dateStr)
                                  }
                                  className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                                    !cell
                                      ? "invisible"
                                      : selectedSpecificDates.includes(
                                            cell.dateStr,
                                          )
                                        ? "bg-base-content text-base-100"
                                        : "bg-base-200 text-base-content/80 hover:bg-base-300"
                                  }`}
                                >
                                  {cell?.day}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                        {selectedSpecificDates.length > 0 && (
                          <div className="mb-4 p-3 bg-base-200 rounded-lg">
                            <p className="text-xs text-base-content/60 mb-1">
                              This will create:
                            </p>
                            <p className="text-sm font-medium text-base-content">
                              {selectedSpecificDates.length} session
                              {selectedSpecificDates.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Back and Save Buttons */}
                <div
                  className="p-8 flex-shrink-0 relative z-50 flex items-center gap-3 mt-auto"
                  style={{
                    pointerEvents: "auto",
                    touchAction: "manipulation",
                  }}
                >
                  <button
                    onClick={() => setCurrentStep(1)}
                    disabled={loading}
                    className="bg-base-200 text-base-content py-4 px-6 font-grotesk font-bold rounded-xl hover:bg-base-300 disabled:opacity-50"
                    style={{ touchAction: "manipulation" }}
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                      }
                      setTimeout(() => {
                        if (recurringMode === "single") {
                          handlePlanSession();
                        } else {
                          handleCreateRecurringSessions();
                        }
                      }, 50);
                    }}
                    disabled={loading || !isStep2Valid}
                    className="bg-primary text-white py-4 px-6 font-grotesk font-bold flex-1 rounded-xl disabled:bg-primary/30 disabled:text-white/50 disabled:cursor-not-allowed"
                    style={{ touchAction: "manipulation" }}
                  >
                    {loading
                      ? "Saving..."
                      : recurringMode === "single"
                        ? "Save"
                        : `Schedule ${getRecurringDates().length} Session${getRecurringDates().length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reset Confirmation Dialog */}
      <Transition appear show={showResetConfirmation} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setShowResetConfirmation(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-base-100 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-base-content font-grotesk"
                  >
                    Reset All Fields?
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-base-content/60 font-sans">
                      Are you sure you want to reset all fields? This will clear
                      the date and all activities. This action cannot be undone.
                    </p>
                  </div>

                  <div className="mt-4 flex gap-3 justify-end">
                    <button
                      type="button"
                      className={TEXT_BUTTON_CLASS}
                      onClick={() => setShowResetConfirmation(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 font-grotesk"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default PlanSessionModal;
